import { buttonVariants } from "@/common/components/Button/styles";
import DropdownMenu from "@/common/components/DropdownMenu";
import { FileUploader } from "@/common/components/FileUploader";
import ResumeBox from "@/common/components/ResumeBox";
import TextField from "@/common/components/TextField";
import { EstablishmentCompanyData } from "@/common/types/establishment/company.type";
import { cn } from "@/common/utils/cn";
import { formatDate } from "@/common/utils/formatters";
import { getFilenameFromUrl } from "@/common/utils/getFilenameFromUrl";
import { parse } from "date-fns";
import { useState } from "react";
import UpdateMaintenanceRequiredDocument from "./UpdateMaintenanceRequiredDocument";
import DeleteMaintenanceRequiredDocument from "./DeleteMaintenanceRequiredDocument";

interface MaintenanceRequiredDocumentItemProps {
  establishmentCompany?: EstablishmentCompanyData;
  maintenanceRequiredDocument: EstablishmentCompanyData['maintenance_required_documents'][number];
  disabled?: boolean;
}

const MaintenanceRequiredDocumentItem = ({
  establishmentCompany,
  maintenanceRequiredDocument,
  disabled,
}: MaintenanceRequiredDocumentItemProps) => {
  const [updateIsOpen, setUpdateIsOpen] = useState(false);
  const [deleteIsOpen, setDeleteIsOpen] = useState(false);

  const validUntil = maintenanceRequiredDocument.valid_until;

  const issueDate = maintenanceRequiredDocument.issue_date;

  const fileAttributes = maintenanceRequiredDocument.file;

  return (
    <div className="flex items-start lg:items-center gap-3 bg-white-100 p-3 rounded-lg lg:bg-transparent lg:p-0 lg:rounded-none">
      <div className="flex flex-col lg:flex-row flex-1 gap-3">
        <div className="flex flex-col gap-1">
          <FileUploader
            title="Anexe documentos"
            className="min-h-[108px] w-[192px]"
            accept=".png, .jpg, .webp, .pdf"
            disabled
            maxFileSizeInMb={20}
            currentFileUrl={fileAttributes?.url}
            fileName={fileAttributes?.url && getFilenameFromUrl(fileAttributes?.url)}
          />
        </div>

        <div className="flex flex-col gap-2 flex-1">
          <div className="bg-white-100 rounded-md flex items-center justify-between p-3">
            <ResumeBox
              label="Nome do documento"
              value={maintenanceRequiredDocument.document.display_name}
              boxClassName="p-0 bg-transparent"
              labelClassName="text-xs"
              valueClassName="text-sm font-bold"
            />

            <DropdownMenu
              triggerLabel="Opções"
              triggerClassName={cn(buttonVariants({ variant: 'neutral' }))}
              menuPositionAlign="end"
              disabled={disabled}
              options={[
                {
                  label: 'Editar',
                  onClick: () => setUpdateIsOpen(true),
                  operationKlassToCheckPermission: 'Admin::Establishment::Companies::UpdateMaintenanceModels',
                },
                {
                  label: 'Remover',
                  onClick: () => setDeleteIsOpen(true),
                  className: '*:text-danger-soft',
                  operationKlassToCheckPermission: 'Admin::Establishment::Companies::UpdateMaintenanceModels',
                },
              ]}
            />

            <UpdateMaintenanceRequiredDocument
              establishmentCompany={establishmentCompany}
              maintenanceRequiredDocument={maintenanceRequiredDocument}
              isOpen={updateIsOpen}
              setIsOpen={setUpdateIsOpen}
            />

            <DeleteMaintenanceRequiredDocument
              establishmentCompany={establishmentCompany}
              maintenanceRequiredDocumentId={maintenanceRequiredDocument.id}
              isOpen={deleteIsOpen}
              setIsOpen={setDeleteIsOpen}
            />
          </div>

          <div className="flex flex-col xl:flex-row gap-3">
            <TextField
              label="Data de emissão"
              disabled
              className="flex-1"
              value={issueDate && formatDate(parse(issueDate, 'yyyy-MM-dd', new Date()))}
            />

            <TextField
              label="Validade do documento"
              disabled
              className="flex-1"
              value={validUntil && formatDate(parse(validUntil, 'yyyy-MM-dd', new Date()))}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default MaintenanceRequiredDocumentItem;
